import { customElement, property, query, state } from 'lit/decorators.js';
import { html, LitElement, nothing, TemplateResult } from 'lit';
import { styles } from '../view/styles';
import { InitialDropdownDetails } from '../../userDetails/types/types';
import { GzSingleSelectComplexWebComponent } from '../../../../../ui/gzSingleSelectComplex';
import { DeviceExtraDetailsWebComponent } from '../../deviceExtraDetails';
import { translateFn } from '../../../../../utils/translator.helper';
import { API_TYPE_USER_FILTER_RISK_MANAGEMENT } from '../../../../../webcomponents.constants';
import { FontSizes, FontWeights, LabelWidthValues } from '../../../../../types/common/types';
import {
  getFindingsDetailsItemType,
  getIconNameItemType,
  getOptionsForInlineMenu,
  renderEmptyDeviceDetailsState,
  renderErrorUserDetailsState,
  RiskManagementSideMenuEvents,
  setFilterOptionsForUserDropdown,
} from '../../../common/common';
import { handleInlineMenuSelectionChangedExtraDetailsPanel } from '../../../../common/utils';
import { GzTooltip } from '../../../../../ui/gzTooltip';

/**
 * @summary Type here the description of this component
 * @tag device-extra-details
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('user-extra-details')
export class UserExtraDetailsWebComponent extends LitElement {
  @property({ type: Object, attribute: false })
  initialDropdownDetails = null as null | InitialDropdownDetails;

  @property({ type: Object, attribute: false })
  items = null as null | Array<any>;

  @property({ type: Number, attribute: false })
  itemsType = null as null | Number;

  @property({ type: Object, attribute: false })
  searchedValue = '';

  @property({ type: Boolean, attribute: true, reflect: true })
  hasErrors = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  showUserDropdown = false;

  @property({ type: String, attribute: false })
  companyId = null as null | String;

  @query('#user-extra-details-selection')
  public readonly userDetailsDropdown!: GzSingleSelectComplexWebComponent;

  @state()
  private tooltipShown;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('mouseleave', this.handleTooltipMouseLeave);
  }

  disconnectedCallback(): void {
    this.removeEventListener('mouseleave', this.handleTooltipMouseLeave);
    super.disconnectedCallback();
  }

  updated(changedProperties): void {
    super.updated(changedProperties);

    if (changedProperties.has('initialDropdownDetails')) {
      this.updateComplete.then(() => {
        setFilterOptionsForUserDropdown(this);
      });
    }
  }

  render() {
    if (this.hasErrors) {
      return renderErrorUserDetailsState();
    }

    if (this.showUserDropdown && !this.initialDropdownDetails) {
      return html` <gz-loader></gz-loader>`;
    }

    return html` ${this.renderUserDropdown()} ${this.renderItems()} `;
  }

  private renderUserDropdown() {
    if (!this.showUserDropdown) {
      return nothing;
    }

    return html`
      <gz-single-select-complex
        id="user-extra-details-selection"
        class="pb-2 w-full user-details-selection"
        .apiType=${API_TYPE_USER_FILTER_RISK_MANAGEMENT}
        .label=${'Identity name'}
        .labelWidth=${LabelWidthValues.FULL}
        .noSearchResultsString=${translateFn('No results')}
        .confirmActionButtonString=${translateFn('Apply')}
        .searchPlaceholderString=${translateFn('Search here')}
        .errorMessage=${translateFn('Error')}
        @selectionChange=${this.handleDropdownValueChanged}
        isFilter
        cannotDelete="true"
      ></gz-single-select-complex>
    `;
  }
  private handleDropdownValueChanged(event) {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.USER_EXTRA_DETAILS_CHANGED, {
        detail: {
          userId: event.detail.selectedValue,
          companyId: this.initialDropdownDetails?.companyId,
          itemsType: this.itemsType,
          indicatorId: this.initialDropdownDetails?.indicatorId,
        },
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  private renderItems() {
    if (!this.items) {
      return html` <gz-loader></gz-loader>`;
    }

    const filteredItems = this.items.filter((item: object) => {
      for (const [key, details] of Object.entries(item)) {
        const isItemFound = key.toString().toLowerCase().includes(this.searchedValue.toLowerCase());

        if (isItemFound) {
          return true;
        }

        const itemDetails: object = details;
        for (const [keyExtraDetails, extraDetails] of Object.entries(itemDetails)) {
          const isItemFoundInExtraDetailsKey = keyExtraDetails
            .toString()
            .toLowerCase()
            .includes(this.searchedValue.toLowerCase());

          if (isItemFoundInExtraDetailsKey) {
            return true;
          }

          const isItemFoundInExtraDetails = extraDetails
            .toString()
            .toLowerCase()
            .includes(this.searchedValue.toLowerCase());

          if (isItemFoundInExtraDetails) {
            return true;
          }
        }
      }

      return false;
    });

    const result: any = [];

    if (this.itemsType) {
      result.push(html`<div class="d-flex flex-row align-items-center pb-2 subtitle">
        <gz-icon name="${getIconNameItemType(this.itemsType)}" class="pr-1" color="secondary"></gz-icon>
        <gz-text class="text-uppercase" fontSize="${FontSizes.PARAGRAPH_11}" fontWeight="${FontWeights.SEMIBOLD}"
          >${getFindingsDetailsItemType(this.itemsType)} (${filteredItems.length})
        </gz-text>
      </div>`);
    }

    result.push(html`<gz-input-search
      class="pb-2"
      value="${this.searchedValue}"
      placeholder=${translateFn('Search {ITEMS_TYPE}', { ITEMS_TYPE: getFindingsDetailsItemType(this.itemsType) })}
      @change="${this.handleSearchChanged}"
    ></gz-input-search>`);

    if (filteredItems.length === 0) {
      result.push(renderEmptyDeviceDetailsState(this.showUserDropdown));
      return result;
    }

    const container: any = [];

    for (const item of filteredItems) {
      for (const [key, details] of Object.entries(item)) {
        const extraDetails: any = details;
        const hasDetails = Object.keys(extraDetails).length > 0;
        let template: TemplateResult;

        if (!hasDetails) {
          template = html` <div class="d-flex flex-row align-items-center ml-1">
            <gz-tooltip class="flex-1" tooltipText="${key}" isEllipsis>
              <gz-text class="d-inline text-details" fontSize="12"> ${key} </gz-text>
            </gz-tooltip>
            ${this.renderSelectionMenu(item, hasDetails)}
          </div>`;
        } else {
          template = html`<gz-labeled-content class="ml-1 mr-1 item-details" label="${key}">
            <div class="d-flex flex-row pr-1">
              <div class="d-flex flex-column item-details-container flex-1 justify-space-between">
                ${this.renderItemsDetails(details)}
              </div>
              ${this.renderSelectionMenu(item, hasDetails)}
            </div>
          </gz-labeled-content>`;
        }

        container.push(template);
      }
    }

    result.push(html`<div class="user-details-container scrollable">${container}</div>`);
    return result;
  }

  private renderSelectionMenu(item, hasDetails: boolean) {
    return html`<gz-select
      class="side-menu-select pl-1"
      options="${JSON.stringify(getOptionsForInlineMenu(this.itemsType))}"
      @selectionChanged=${e =>
        handleInlineMenuSelectionChangedExtraDetailsPanel(this, e.detail.selected, item, hasDetails, e)}
    >
      <gz-icon-button icon="ds-icon-more" fontSize="24" buttonType="square" buttonFill="quiet" buttonColor="secondary">
      </gz-icon-button>
    </gz-select>`;
  }

  private renderItemsDetails(itemDetails) {
    const container: any = [];
    for (const [key, details] of Object.entries(itemDetails)) {
      const template: TemplateResult = html`<gz-tooltip class="w-full" tooltipText="${key}: ${details}" isEllipsis>
        <gz-text class="d-inline" fontSize="${FontSizes.PARAGRAPH_12}">${key}:${details}</gz-text>
      </gz-tooltip>`;
      container.push(template);
    }
    return container;
  }

  handleSearchChanged(event: CustomEvent): void {
    this.searchedValue = event.detail.sourceEvent;
  }

  private handleTooltipMouseLeave(): void {
    clearTimeout(this.tooltipShown);
    GzTooltip.hide();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'device-extra-details': DeviceExtraDetailsWebComponent;
  }
}
